import { AnimatePresence, motion } from 'framer-motion';
import { Check, Undo2 } from 'lucide-react';
import type { ReactNode } from 'react';

import { formatShortDate } from '../../../../utils/stringHelpers';
import { Button } from '../../../Button';
import { EASE_OUT, StatusDot } from '../../../portal';

interface StoreListingActionsProps {
  formId: string;
  isInitialDraft?: boolean;
  hasChanges: boolean;
  canPublish: boolean;
  hasDraft: boolean;
  justSavedDraft: boolean;
  justPublished: boolean;
  isSubmitting: boolean;
  isSavingDraft: boolean;
  lastPublishedAt: string | null;
  onDiscard: () => void;
  onSaveDraft: () => void;
}

const LABEL_FADE = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.12, ease: EASE_OUT } },
  exit: { opacity: 0, transition: { duration: 0.12, ease: EASE_OUT } },
};

/** Crossfades button copy ("Save Draft" ⇄ "✓ Save Draft"); the button keeps its min-width. */
function SwapLabel({ id, children }: { id: string; children: ReactNode }) {
  return (
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.span
        key={id}
        {...LABEL_FADE}
        className="flex items-center gap-2 whitespace-nowrap"
      >
        {children}
      </motion.span>
    </AnimatePresence>
  );
}

const ENTER_CLASS = 'animate-in fade-in ease-out animation-duration-150';

export function StoreListingActions({
  formId,
  isInitialDraft = false,
  hasChanges,
  canPublish,
  hasDraft,
  justSavedDraft,
  justPublished,
  isSubmitting,
  isSavingDraft,
  lastPublishedAt,
  onDiscard,
  onSaveDraft,
}: StoreListingActionsProps) {
  const statusText = justPublished
    ? 'Listing published just now'
    : hasDraft
      ? 'Draft saved, not published'
      : lastPublishedAt
        ? `Last published ${formatShortDate(lastPublishedAt)}`
        : null;

  return (
    <>
      {!hasChanges && statusText ? (
        <span
          key={statusText}
          className={`flex items-center gap-1 text-sm text-seeker-400 ${ENTER_CLASS}`}
        >
          <StatusDot />
          {statusText}
        </span>
      ) : null}
      {hasChanges || hasDraft ? (
        <Button
          variant="ghostMuted"
          className={ENTER_CLASS}
          disabled={isSubmitting || isSavingDraft}
          onClick={onDiscard}
        >
          <Undo2 className="size-4" />
          Discard changes
        </Button>
      ) : null}

      <Button
        variant={justSavedDraft ? 'outlineSuccess' : 'outlineWhite'}
        className="relative min-w-[140px]"
        disabled={!hasChanges || isSavingDraft || isSubmitting}
        submitting={isSavingDraft}
        onClick={onSaveDraft}
      >
        <SwapLabel id={justSavedDraft ? 'saved' : 'save'}>
          {justSavedDraft ? <Check className="size-4" /> : null}
          Save Draft
        </SwapLabel>
      </Button>

      <Button
        type="submit"
        form={formId}
        variant={justPublished ? 'outlineSuccess' : 'primary'}
        className="relative min-w-[140px]"
        disabled={!canPublish || isSubmitting || isSavingDraft}
        submitting={isSubmitting}
      >
        <SwapLabel id={justPublished ? 'published' : 'publish'}>
          {justPublished ? (
            <>
              <Check className="size-4" />
              Published
            </>
          ) : isInitialDraft ? (
            'Save and continue'
          ) : (
            'Publish Updates'
          )}
        </SwapLabel>
      </Button>
    </>
  );
}
